import React, { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import styles from "../read.module.css";

const schema = yup.object().shape({
  name: yup.string().trim().required("Please enter your name."),
  email: yup.string().trim().required("Please enter a valid email address.").email("Please enter a valid email address."),
});

const CheckIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#C94A31" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>
);

const SignupForm = ({
  source = "read",
  title = "Reserve your test drive",
  subtitle = "Spots are limited while we onboard early teams.",
  buttonText = "Get early access",
  note = "We'll only use your email to set up your test drive.",
  successText = "with next steps for your PropertyDek test drive.",
  onDone,
}) => {
  const [submittedData, setSubmittedData] = useState(null);
  const [submitError, setSubmitError] = useState("");

  const { register, handleSubmit, errors, reset, formState } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
    defaultValues: { name: "", email: "" },
  });

  const onSubmit = async (data) => {
    setSubmitError("");
    try {
      await axios.post(process.env.REACT_APP_USER_API_URL + "v1/contact-us-new", { name: data.name.trim(), email: data.email.trim(), source });
      setSubmittedData(data);
    } catch (error) {
      setSubmitError("Something went wrong. Please try again.");
    }
  };

  const onReset = () => {
    reset({ name: "", email: "" });
    setSubmittedData(null);
  };

  if (submittedData) {
    const firstName = (submittedData.name || "").trim().split(/\s+/)[0] || "friend";
    return (
      <div className={styles.SuccessBox}>
        <div className={styles.SuccessIcon}><CheckIcon /></div>
        <h2 className={styles.CardTitle}>You're on the list, {firstName}!</h2>
        <p className={styles.SuccessText}>We'll reach out at <strong>{submittedData.email}</strong> {successText}</p>
        {onDone ? (
          <button type="button" className={styles.SecondaryBtn} onClick={onDone}>Close</button>
        ) : (
          <button type="button" className={styles.SecondaryBtn} onClick={onReset}>Sign up someone else</button>
        )}
      </div>
    );
  }

  return (
    <form className={styles.Form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className={styles.CardHead}>
        <h2 className={styles.CardTitle}>{title}</h2>
        <p className={styles.CardSub}>{subtitle}</p>
      </div>
      <div className={styles.Field}>
        <label htmlFor="pd-name">Full name</label>
        <input id="pd-name" name="name" type="text" autoComplete="name" placeholder="Jordan Rivera" ref={register} className={errors.name ? styles.InputError : ""} aria-invalid={!!errors.name} />
        {errors.name && <p role="alert" className={styles.ErrorText}>{errors.name.message}</p>}
      </div>
      <div className={styles.Field}>
        <label htmlFor="pd-email">Work email</label>
        <input id="pd-email" name="email" type="email" autoComplete="email" placeholder="jordan@yourcompany.com" ref={register} className={errors.email ? styles.InputError : ""} aria-invalid={!!errors.email} />
        {errors.email && <p role="alert" className={styles.ErrorText}>{errors.email.message}</p>}
      </div>
      <button type="submit" className={styles.PrimaryBtn} disabled={formState.isSubmitting}>{formState.isSubmitting ? "Submitting…" : buttonText}</button>
      {submitError && <p role="alert" className={`${styles.ErrorText} ${styles.SubmitError}`}>{submitError}</p>}
      <p className={styles.FormNote}>{note}</p>
    </form>
  );
};

export default SignupForm;
